/**
 * MCPServerCard - Display card for an MCP server configuration
 */

import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Pencil, Trash2 } from 'lucide-react'
import { Button } from '@/components/base/buttons'
import type { MCPServerConfigFrontend, Language } from '@/types/integrations'
import { useMcpDiscovery } from '@/hooks/api/useIntegrations'

interface MCPServerCardProps {
  server: MCPServerConfigFrontend
  index: number
  language: Language
  onEdit: (server: MCPServerConfigFrontend, index: number) => void
  onDelete: (index: number, displayName?: string) => void
}

export function MCPServerCard({ server, index, language, onEdit, onDelete }: MCPServerCardProps) {
  const { t } = useTranslation()
  const { data, refetch } = useMcpDiscovery(server.url)
  const serverInfo = data?.server_info

  // Fetch discovery data on mount
  useEffect(() => {
    if (server.url) {
      refetch()
    }
  }, [server.url])

  const displayTitle =
    serverInfo?.name ||
    server.alias ||
    (server.url ? new URL(server.url, window.location.origin).hostname : t('resources.assistants.integrations.mcp_server'))
  const iconPath = serverInfo?.icon || (server as any)?.icon || '/images/mcp.svg'

  const summaryItems = [
    { label: t('resources.assistants.integrations.mcp.url_label'), value: server.url || '—' },
    { label: t('resources.assistants.integrations.mcp.type_label'), value: server.type || 'streamable_http' },
    { label: t('resources.assistants.integrations.mcp.alias_label'), value: server.alias || '—' },
    { label: t('resources.assistants.integrations.mcp.headers_label'), value: String(Object.keys(server.headers || {}).length) },
    { label: t('resources.assistants.integrations.mcp.config_fields_label'), value: String(Object.keys(server.config || {}).length) },
  ]

  return (
    <div className="mb-3 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-center">
        {/* Left: Icon + Title + Tags */}
        <div className="flex shrink-0 items-center gap-3" style={{ minWidth: 200, width: 220 }}>
          <div className="flex h-10 w-10 shrink-0 items-center justify-center">
            <img
              src={iconPath}
              alt="MCP"
              className="h-8 w-8 rounded-md object-contain"
            />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              {displayTitle}
            </h3>
            <div className="mt-1 flex flex-wrap gap-1">
              {serverInfo?.tags && serverInfo.tags.length > 0 ? (
                serverInfo.tags.slice(0, 3).map((tag: string, i: number) => (
                  <span
                    key={`${tag}-${i}`}
                    className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                  >
                    {tag}
                  </span>
                ))
              ) : (
                <span className="rounded-full bg-primary px-2 py-0.5 text-xs font-semibold text-white">
                  mcp
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="mx-4 h-12 w-px bg-gray-200 dark:bg-gray-700" />

        {/* Middle: Summary */}
        <div className="min-w-0 flex-1">
          <p className="mb-1 text-sm font-semibold text-gray-900 dark:text-gray-100">{t('resources.assistants.integrations.summary')}</p>
          <div className="flex gap-6">
            <div className="min-w-0 flex-1">
              {summaryItems.slice(0, 2).map((item, i) => (
                <div
                  key={i}
                  className="flex min-w-0 items-center gap-1 overflow-hidden"
                  title={`${item.label} ${item.value}`}
                >
                  <span className="shrink-0 text-xs leading-tight text-gray-500 dark:text-gray-400">
                    {item.label}
                  </span>
                  <span className="min-w-0 truncate text-xs leading-tight text-gray-900 dark:text-gray-100">
                    {item.value}
                  </span>
                </div>
              ))}
            </div>
            <div className="min-w-0 flex-1">
              {summaryItems.slice(2).map((item, i) => (
                <div
                  key={i}
                  className="flex min-w-0 items-center gap-1 overflow-hidden"
                  title={`${item.label} ${item.value}`}
                >
                  <span className="shrink-0 text-xs leading-tight text-gray-500 dark:text-gray-400">
                    {item.label}
                  </span>
                  <span className="min-w-0 truncate text-xs leading-tight text-gray-900 dark:text-gray-100">
                    {item.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="mx-4 h-12 w-px bg-gray-200 dark:bg-gray-700" />

        {/* Right: Actions */}
        <div className="flex shrink-0 gap-1">
          <Button
            type="button"
            color="tertiary"
            size="sm"
            onPress={() => onEdit(server, index)}
            className="h-8 w-8 p-0"
            iconLeading={Pencil}
          />
          <Button
            type="button"
            color="tertiary"
            size="sm"
            onPress={() => onDelete(index, displayTitle)}
            className="h-8 w-8 p-0 text-red-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
            iconLeading={Trash2}
          />
        </div>
      </div>
    </div>
  )
}
